import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox
} from '@mui/material';
// Toggle switch for activate/inactivate
import { Switch } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as ViewIcon,
  CreditCard as CardIcon,
  WorkspacePremium as CertificateIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { apiServices } from '../../../services/apiServices';
import { countryCodes } from '../../../utils/countryCodes';
import Swal from 'sweetalert2';
import { Configuration } from '../../../assets/data/config';
import imgUrl from '../../../assets/images/imgUrl';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { jsPDF } from 'jspdf';

const EntityManagement = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get admin data from localStorage to check usertype
  const adminDataStr = localStorage.getItem('adminData');
  const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
  const usertype = adminData?.usertype;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Form states for creating/editing entity
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    country: '',
    usertype: 'Entity', // Default to "Entity" for entity management
    entity_discount_percent: '',
    categoryname: '',
    entity_payment_tier: '', // PAID or UNPAID tier
    payment_type: '',
    gender: '',
    dob: '',
    discount_coupon_percent: '',
    reference_by: '',
    payment_status: 'PENDING',
    effective_date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
    mobile_email_flag: false // Email and website visibility on Certificate/Visiting card
  });
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Profile image states
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [imageUploadError, setImageUploadError] = useState('');

  // Edit modal profile image states
  const [editProfileImage, setEditProfileImage] = useState(null);
  const [editProfileImagePreview, setEditProfileImagePreview] = useState(null);
  const [editImageUploadError, setEditImageUploadError] = useState('');
  const [currentProfileImage, setCurrentProfileImage] = useState(null);
  const [loadingCurrentImage, setLoadingCurrentImage] = useState(false);

  // Visiting Card and Certificate states
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [selectedEntityForCard, setSelectedEntityForCard] = useState(null);
  const [cardImage, setCardImage] = useState(null);
  const [certificateImage, setCertificateImage] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [certificateLoading, setCertificateLoading] = useState(false);

  // Carousel state for visiting card images (4 pages: 1 dynamic + 3 static)
  const [currentCardPage, setCurrentCardPage] = useState(0);

  // More options menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEntityForMenu, setSelectedEntityForMenu] = useState(null);

  // Entity Referrals Modal states
  const [referralsModalOpen, setReferralsModalOpen] = useState(false);
  const [selectedEntityForReferrals, setSelectedEntityForReferrals] = useState(null);
  const [referralsData, setReferralsData] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState('');
  const [referralsPage, setReferralsPage] = useState(0); // 0-based for MUI pagination
  const [referralsRowsPerPage, setReferralsRowsPerPage] = useState(10);
  const [referralsSearchTerm, setReferralsSearchTerm] = useState('');
  const [referralsPaymentFilter, setReferralsPaymentFilter] = useState('');
  const [referralsActiveFilter, setReferralsActiveFilter] = useState('');

  // Options for dropdowns
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);

  // Fetch entities from API
  const fetchEntities = async (pageNum = 0, limit = 1000) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: 1, // Fetch all data at once
        limit: 100 // Get all entities to filter properly
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (countryFilter) {
        params.append('country', countryFilter);
      }

      if (categoryFilter) {
        params.append('categoryname', categoryFilter);
      }

      if (paymentStatusFilter) {
        params.append('payment_status', paymentStatusFilter);
      }

      if (fromDate) {
        // Ensure date is in yyyy-mm-dd format
        const formattedFromDate = new Date(fromDate).toISOString().split('T')[0];
        params.append('date_from', formattedFromDate);
      }

      if (toDate) {
        // Ensure date is in yyyy-mm-dd format
        const formattedToDate = new Date(toDate).toISOString().split('T')[0];
        params.append('date_to', formattedToDate);
      }

      // Add sorting by ID ascending
      params.append('sortBy', 'userid');
      params.append('sortOrder', 'ASC');

      const response = await apiServices.getAPI(`entities/listing?${params.toString()}`);

      if (response.data.success) {
        // Filter entities to only show those matching logged-in admin's email when usertype is Entity
        let filteredEntities = response.data.data.entities;

        if (usertype === 'Entity') {
          filteredEntities = response.data.data.entities.filter(entity =>
            entity.email === adminData?.email || entity.emailid === adminData?.email
          );
        }

        // Apply pagination on the filtered results
        const startIndex = pageNum * limit;
        const endIndex = startIndex + limit;
        const paginatedEntities = filteredEntities.slice(startIndex, endIndex);

        setEntities(paginatedEntities);
        setTotalCount(filteredEntities.length);
      } else {
        setError('Failed to fetch entities');
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError('Error fetching entities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await apiServices.getAPI('categories');
      if (response.data.success) {
        setCategories(response.data.data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch countries for dropdown
  const fetchCountries = async () => {
    try {
      const response = await apiServices.getAPI('countries');
      if (response.data.countries && Array.isArray(response.data.countries)) {
        setCountries(response.data.countries);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setPage(0);
    fetchEntities(0, rowsPerPage);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCountryFilter('');
    setCategoryFilter('');
    setPaymentStatusFilter('');
    setFromDate('');
    setToDate('');
    setPage(0);
    fetchEntities();
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      country: '',
      usertype: 'Entity',
      entity_discount_percent: '',
      categoryname: '',
      entity_payment_tier: '',
      payment_type: '',
      gender: '',
      dob: '',
      discount_coupon_percent: '',
      reference_by: '',
      payment_status: 'PENDING'
    });
    setCountryCode('+91');
    setPhoneNumber('');
    setProfileImage(null);
    setProfileImagePreview(null);
    setImageUploadError('');
    setCreateModalOpen(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setFormData({
      name: '',
      phone: '',
      email: '',
      country: '',
      usertype: 'Entity',
      entity_discount_percent: '',
      categoryname: '',
      entity_payment_tier: '',
      payment_type: '',
      gender: '',
      dob: '',
      discount_coupon_percent: '',
      reference_by: '',
      payment_status: 'PENDING',
      effective_date: new Date().toISOString().split('T')[0],
      mobile_email_flag: false
    });
    setCountryCode('+91');
    setPhoneNumber('');
    setProfileImage(null);
    setProfileImagePreview(null);
    setImageUploadError('');
  };

  // Open edit modal
  const handleOpenEditModal = (entity) => {
    setSelectedEntity(entity);
    setFormData({
      name: entity.username || '',
      phone: entity.mobile || '',
      email: entity.email || '',
      country: entity.country || '',
      usertype: entity.usertype || 'Entity',
      entity_discount_percent: entity.entity_discount_percent || '',
      categoryname: entity.categoryname || '',
      entity_payment_tier: entity.entity_payment_tier || '',
      payment_type: entity.payment_type || '',
      gender: entity.gender || '',
      dob: entity.dob || '',
      discount_coupon_percent: entity.discount_coupon_percent || '',
      reference_by: entity.reference_by || '',
      payment_status: entity.payment_status || 'PENDING',
      mobile_email_flag: entity.mobile_email_flag === 'Y' || entity.mobile_email_flag === true || false
    });
    setEditProfileImage(null);
    setEditProfileImagePreview(null);
    setEditImageUploadError('');

    // Fetch current profile image
    if (entity.userid) {
      fetchCurrentProfileImage(entity.userid);
    }

    setEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedEntity(null);
    setEditProfileImage(null);
    setEditProfileImagePreview(null);
    setEditImageUploadError('');

    // Clean up blob URLs to prevent memory leaks
    if (currentProfileImage && currentProfileImage.startsWith('blob:')) {
      URL.revokeObjectURL(currentProfileImage);
    }
    setCurrentProfileImage(null);
  };

  // Open view modal
  const handleOpenViewModal = (entity) => {
    setSelectedEntity(entity);
    setViewModalOpen(true);
  };

  // Close view modal
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedEntity(null);
  };

  // Handle more options menu
  const handleMenuOpen = (event, entity) => {
    setAnchorEl(event.currentTarget);
    setSelectedEntityForMenu(entity);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEntityForMenu(null);
  };

  // Handle view entity from menu
  const handleViewEntityFromMenu = () => {
    if (selectedEntityForMenu) {
      handleOpenViewModal(selectedEntityForMenu);
    }
    handleMenuClose();
  };

  // Handle edit entity from menu
  const handleEditEntityFromMenu = () => {
    if (selectedEntityForMenu) {
      handleOpenEditModal(selectedEntityForMenu);
    }
    handleMenuClose();
  };

  // Handle view referrals from menu
  const handleViewReferrals = (entity) => {
    setSelectedEntityForReferrals(entity);
    setReferralsModalOpen(true);
    setReferralsPage(0);
    setReferralsSearchTerm('');
    setReferralsPaymentFilter('');
    setReferralsActiveFilter('');
    fetchEntityReferrals(entity.peacekeeper_id, 0, referralsRowsPerPage);
    handleMenuClose();
  };

  // Fetch entity referrals from API
  const fetchEntityReferrals = async (peacekeeperId, pageNum = 0, limit = 10) => {
    setReferralsLoading(true);
    setReferralsError('');

    try {
      const params = new URLSearchParams({
        page: pageNum + 1, // API uses 1-based pagination
        limit: limit
      });

      if (referralsSearchTerm.trim()) {
        params.append('search', referralsSearchTerm.trim());
      }

      if (referralsPaymentFilter) {
        params.append('payment_status', referralsPaymentFilter);
      }

      if (referralsActiveFilter) {
        params.append('activestatus', referralsActiveFilter);
      }

      const response = await apiServices.getAPI(`entity/referrals/${peacekeeperId}?${params.toString()}`);

      if (response.data.success) {
        setReferralsData(response.data.data);
      } else {
        setReferralsError('Failed to fetch referrals');
      }
    } catch (err) {
      console.error('Error fetching entity referrals:', err);
      setReferralsError(err.response?.data?.error || 'Error fetching referrals. Please try again.');
    } finally {
      setReferralsLoading(false);
    }
  };

  // Handle referrals page change
  const handleReferralsPageChange = (event, newPage) => {
    setReferralsPage(newPage);
    if (selectedEntityForReferrals) {
      fetchEntityReferrals(selectedEntityForReferrals.peacekeeper_id, newPage, referralsRowsPerPage);
    }
  };

  // Handle referrals rows per page change
  const handleReferralsRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setReferralsRowsPerPage(newRowsPerPage);
    setReferralsPage(0);
    if (selectedEntityForReferrals) {
      fetchEntityReferrals(selectedEntityForReferrals.peacekeeper_id, 0, newRowsPerPage);
    }
  };

  // Handle apply referrals filters
  const handleApplyReferralsFilters = () => {
    setReferralsPage(0);
    if (selectedEntityForReferrals) {
      fetchEntityReferrals(selectedEntityForReferrals.peacekeeper_id, 0, referralsRowsPerPage);
    }
  };

  // Handle refresh referrals
  const handleRefreshReferrals = () => {
    setReferralsSearchTerm('');
    setReferralsPaymentFilter('');
    setReferralsActiveFilter('');
    setReferralsPage(0);
    if (selectedEntityForReferrals) {
      fetchEntityReferrals(selectedEntityForReferrals.peacekeeper_id, 0, referralsRowsPerPage);
    }
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Profile image handling functions
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    setImageUploadError('');

    if (file) {
      // Validate file type - based on API documentation
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setImageUploadError('Only JPEG, PNG, GIF, and WebP images are allowed');
        return;
      }

      // Validate file size (max 5MB as per API docs)
      if (file.size > 5 * 1024 * 1024) {
        setImageUploadError('Image size must be less than 5MB');
        return;
      }

      // Validate minimum dimensions (recommended 50x50 as per API docs)
      const img = new Image();
      img.onload = () => {
        if (img.width < 50 || img.height < 50) {
          setImageUploadError('Image dimensions should be at least 50x50 pixels');
          return;
        }

        // If validation passes, set the image and preview
        setProfileImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfileImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      };
      img.onerror = () => {
        setImageUploadError('Invalid image file');
      };
      img.src = URL.createObjectURL(file);
    }
  };

  // Fetch current profile image for edit modal
  const fetchCurrentProfileImage = async (userId) => {
    setLoadingCurrentImage(true);
    setCurrentProfileImage(null);

    try {
      // Fetch the profile image blob from API
      const response = await apiServices.getBlobAPI(`profile-image/${userId}`);

      if (response.data) {
        const blob = response.data;
        const imageUrl = URL.createObjectURL(blob);
        setCurrentProfileImage(imageUrl);
        console.log('Current profile image loaded successfully');
      }
    } catch (error) {
      console.log('No existing profile image or error fetching:', error);
      // Not an error - user might not have a profile image yet
      setCurrentProfileImage(null);
    } finally {
      setLoadingCurrentImage(false);
    }
  };

  // Edit modal profile image handling
  const handleEditImageUpload = (event) => {
    const file = event.target.files[0];
    setEditImageUploadError('');

    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setEditImageUploadError('Only JPEG, PNG, GIF, and WebP images are allowed');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setEditImageUploadError('Image size must be less than 5MB');
        return;
      }

      // Validate minimum dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width < 50 || img.height < 50) {
          setEditImageUploadError('Image dimensions should be at least 50x50 pixels');
          return;
        }

        // If validation passes, set the image and preview
        setEditProfileImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setEditProfileImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      };
      img.onerror = () => {
        setEditImageUploadError('Invalid image file');
      };
      img.src = URL.createObjectURL(file);
    }
  };

  // Handle create entity
  const handleCreateEntity = async () => {
    if (!formData.name.trim() || !phoneNumber.trim() || !formData.email.trim() || !formData.country.trim()) {
      Swal.fire('Error', 'Name, phone, email, and country are required', 'error');
      return;
    }

    setCreating(true);
    try {
      const formDataToSend = new FormData();

      // Required fields
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('phone', countryCode + phoneNumber.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('country', formData.country.trim());

      // Optional fields
      if (formData.usertype) formDataToSend.append('usertype', formData.usertype);
      if (formData.entity_discount_percent) formDataToSend.append('entity_discount_percent', formData.entity_discount_percent);
      if (formData.categoryname) formDataToSend.append('categoryname', formData.categoryname);
      if (formData.entity_payment_tier) formDataToSend.append('entity_payment_tier', formData.entity_payment_tier);
      if (formData.payment_type) formDataToSend.append('payment_type', formData.payment_type);
      if (formData.gender) formDataToSend.append('gender', formData.gender);
      if (formData.dob) formDataToSend.append('dob', formData.dob);
      if (formData.discount_coupon_percent) formDataToSend.append('discount_coupon_percent', formData.discount_coupon_percent);
      if (formData.reference_by) formDataToSend.append('reference_by', formData.reference_by);
      if (formData.payment_status) formDataToSend.append('payment_status', formData.payment_status);

      // Email and website visibility flag (convert boolean to "Y" or "N")
      formDataToSend.append('mobile_email_flag', formData.mobile_email_flag ? "Y" : "N");

      // Profile image
      if (profileImage) formDataToSend.append('profileImage', profileImage);

      const response = await apiServices.postMultiPartFormDataAPI('users/enhanced', formDataToSend);

      if (response.data.message && response.data.message.includes('successfully')) {
        Swal.fire({
          title: 'Success',
          text: 'Entity created successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        });
        handleCloseCreateModal();
        fetchEntities(page, rowsPerPage);
      } else {
        throw new Error(response.data.error || 'Failed to create entity');
      }
    } catch (err) {
      console.error('Error creating entity:', err);
      Swal.fire('Error', err.message || 'Failed to create entity', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Handle update entity - using Enhanced User Update API
  const handleUpdateEntity = async () => {
    if (!selectedEntity) {
      Swal.fire('Error', 'No entity selected for update', 'error');
      return;
    }

    setUpdating(true);

    try {
      let profileImageUpdated = false;
      let entityDataUpdated = false;

      // Step 1: Upload profile image if changed
      if (editProfileImage) {
        try {
          console.log('Uploading new profile image...');
          const formData = new FormData();
          formData.append('profileImage', editProfileImage);
          formData.append('userId', selectedEntity.userid);
          formData.append('imageType', 'profile');

          const imageResponse = await apiServices.postMultiPartFormDataAPI('upload-profile-image', formData);

          if (imageResponse.status === 200 || imageResponse.status === 201) {
            console.log('Profile image uploaded successfully');
            profileImageUpdated = true;
          }
        } catch (imageError) {
          console.error('Error uploading profile image:', imageError);
          Swal.fire('Warning', 'Profile image upload failed, but will continue with other updates', 'warning');
        }
      }

      // Step 2: Build update payload using Enhanced User Update API format
      const updatePayload = {
        // Identify by peacekeeper_id (more reliable than email)
        peacekeeper_id: selectedEntity.peacekeeper_id
      };

      // Only include changed fields
      if (formData.name && formData.name.trim() !== selectedEntity.username) {
        updatePayload.username = formData.name.trim();
      }
      if (formData.email && formData.email.trim() !== selectedEntity.email) {
        updatePayload.email = formData.email.trim();
      }
      if (formData.phone && formData.phone !== selectedEntity.mobile) {
        updatePayload.mobile = formData.phone;
      }
      if (formData.country && formData.country !== selectedEntity.country) {
        updatePayload.country = formData.country;
      }
      if (formData.dob && formData.dob !== selectedEntity.dob) {
        updatePayload.dob = formData.dob;
      }
      if (formData.gender && formData.gender !== selectedEntity.gender) {
        updatePayload.gender = formData.gender;
      }
      if (formData.usertype && formData.usertype !== selectedEntity.usertype) {
        updatePayload.usertype = formData.usertype;
      }
      if (formData.categoryname !== selectedEntity.categoryname) {
        updatePayload.categoryname = formData.categoryname || null;
      }
      if (formData.entity_payment_tier !== selectedEntity.entity_payment_tier) {
        updatePayload.entity_payment_tier = formData.entity_payment_tier || null;
      }
      if (formData.entity_discount_percent !== selectedEntity.entity_discount_percent) {
        updatePayload.entity_discount_percent = formData.entity_discount_percent || null;
      }
      if (formData.discount_coupon_percent !== selectedEntity.discount_coupon_percent) {
        updatePayload.discount_coupon_percent = formData.discount_coupon_percent || null;
      }
      if (formData.payment_type && formData.payment_type !== selectedEntity.payment_type) {
        updatePayload.payment_type = formData.payment_type;
      }
      if (formData.payment_status && formData.payment_status !== selectedEntity.payment_status) {
        updatePayload.payment_status = formData.payment_status;
      }
      // Check if mobile_email_flag has changed (convert boolean to "Y" or "N" string)
      const currentMobileEmailFlag = selectedEntity.mobile_email_flag === 'Y' || selectedEntity.mobile_email_flag === true;
      if (formData.mobile_email_flag !== currentMobileEmailFlag) {
        updatePayload.mobile_email_flag = formData.mobile_email_flag ? "Y" : "N";
      }

      // Check if there are entity data changes
      if (Object.keys(updatePayload).length > 1) { // More than just peacekeeper_id
        // Call Enhanced User Update API
        const response = await apiServices.putAPI('users/enhanced', updatePayload);

        if (response.data.message && response.data.message.includes('successfully')) {
          entityDataUpdated = true;
        } else {
          throw new Error(response.data.error || 'Failed to update entity');
        }
      }

      // Check if any updates were made
      if (!profileImageUpdated && !entityDataUpdated) {
        Swal.fire('Info', 'No changes detected', 'info');
        setUpdating(false);
        return;
      }

      // Step 3: Regenerate visiting card and certificate after successful update
      console.log('Regenerating visiting card and certificate...');

      // Show loading message
      Swal.fire({
        title: 'Success!',
        html: 'Entity updated successfully!<br/>Regenerating visiting card and certificate...',
        icon: 'success',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Regenerate both in parallel
      const regenerationPromises = [];

      // Regenerate visiting card
      regenerationPromises.push(
        apiServices.putAPI(`update-visiting-card/${selectedEntity.peacekeeper_id}`, { forceRegenerate: true })
          .then(() => console.log('Visiting card regenerated successfully'))
          .catch(err => console.error('Error regenerating visiting card:', err))
      );

      // Regenerate certificate
      regenerationPromises.push(
        apiServices.putAPI(`update-certificate/${selectedEntity.peacekeeper_id}`, { forceRegenerate: true })
          .then(() => console.log('Certificate regenerated successfully'))
          .catch(err => console.error('Error regenerating certificate:', err))
      );

      // Wait for regeneration to complete (max 5 seconds)
      await Promise.race([
        Promise.all(regenerationPromises),
        new Promise(resolve => setTimeout(resolve, 5000))
      ]);

      // Show final success message
      Swal.fire({
        title: 'Complete!',
        text: 'Entity updated and documents regenerated successfully!',
        icon: 'success',
        showConfirmButton: false,
        timer: 2000
      });

      handleCloseEditModal();
      fetchEntities(page, rowsPerPage);

    } catch (err) {
      console.error('Error updating entity:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update entity';
      Swal.fire('Error', errorMessage, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete entity
  const handleDeleteEntity = async (entity) => {
    const result = await Swal.fire({
      title: 'Delete Entity?',
      text: `Are you sure you want to delete "${entity.username}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      Swal.fire({
        title: 'Deleting...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await apiServices.deleteAPI(`entities/${entity.userid}`);

      if (response.data.success) {
        Swal.fire({
          title: 'Deleted!',
          text: 'Entity has been deleted successfully.',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        });
        fetchEntities(page, rowsPerPage);
      } else {
        throw new Error(response.data.message || 'Failed to delete entity');
      }
    } catch (err) {
      console.error('Error deleting entity:', err);
      Swal.fire('Error', err.message || 'Failed to delete entity', 'error');
    }
  };

  // Handle export entities
  const handleExportEntities = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Starting export of all entities...');

      const params = new URLSearchParams({
        page: 1,
        limit: 100
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (countryFilter) {
        params.append('country', countryFilter);
      }
      if (categoryFilter) {
        params.append('categoryname', categoryFilter);
      }
      if (paymentStatusFilter) {
        params.append('payment_status', paymentStatusFilter);
      }
      if (fromDate) {
        // Ensure date is in yyyy-mm-dd format
        const formattedFromDate = new Date(fromDate).toISOString().split('T')[0];
        params.append('date_from', formattedFromDate);
      }
      if (toDate) {
        // Ensure date is in yyyy-mm-dd format
        const formattedToDate = new Date(toDate).toISOString().split('T')[0];
        params.append('date_to', formattedToDate);
      }

      const response = await apiServices.getAPI(`entities/listing?${params.toString()}`);

      if (!response.data.success) {
        setError('Failed to fetch entities for export');
        return;
      }

      const allEntities = response.data.data.entities;
      console.log(`Exporting ${allEntities.length} entities...`);

      downloadCSV(allEntities, 'entities_export.csv');

      console.log('Export completed successfully');
    } catch (err) {
      console.error('Error exporting entities:', err);
      setError('Error exporting entities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle viewing visiting card
  const handleViewCard = async (entity) => {
    setSelectedEntityForCard(entity);
    setCardModalOpen(true);
    setCardImage(null);
    setCardLoading(true);
    setCurrentCardPage(0); // Reset to first page

    try {
      const response = await apiServices.getBlobAPI(`visiting-card/${entity.userid}`);

      if (response.data) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        setCardImage(blobUrl);
      }
    } catch (err) {
      console.error('Error fetching visiting card:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load visiting card. Please try again.',
      });
    } finally {
      setCardLoading(false);
    }
  };

  // Handle viewing certificate
  const handleViewCertificate = async (entity) => {
    setSelectedEntityForCard(entity);
    setCertificateModalOpen(true);
    setCertificateImage(null);
    setCertificateLoading(true);

    try {
      const response = await apiServices.getBlobAPI(`certificate/${entity.userid}`);

      if (response.data) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        setCertificateImage(blobUrl);
      }
    } catch (err) {
      console.error('Error fetching certificate:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load certificate. Please try again.',
      });
    } finally {
      setCertificateLoading(false);
    }
  };

  // Navigate to next visiting card page
  const nextCardPage = () => {
    setCurrentCardPage((prev) => (prev + 1) % 4);
  };

  // Navigate to previous visiting card page
  const prevCardPage = () => {
    setCurrentCardPage((prev) => (prev - 1 + 4) % 4);
  };

  // Handle downloading current visiting card
  const handleDownloadCard = async () => {
    if (!selectedEntityForCard) return;

    try {
      let imageToDownload;
      let filename;

      if (currentCardPage === 0) {
        // Dynamic card from API
        if (cardImage) {
          imageToDownload = cardImage;
          filename = `VisitingCard-${selectedEntityForCard.peacekeeper_id || 'Entity'}.png`;
        } else {
          Swal.fire('Error', 'Visiting card not loaded yet', 'error');
          return;
        }
      } else {
        // Static cards
        const staticCards = [null, imgUrl.visitingCard2, imgUrl.visitingCard3, imgUrl.visitingCard4];
        imageToDownload = staticCards[currentCardPage];
        filename = `VisitingCard-${currentCardPage}-${selectedEntityForCard.peacekeeper_id || 'Entity'}.png`;
      }

      // Download the image
      const response = await fetch(imageToDownload);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Downloaded!',
        text: 'Visiting card downloaded successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error downloading visiting card:', error);
      Swal.fire('Error', 'Failed to download visiting card', 'error');
    }
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Handle downloading all visiting cards as PDF
  const handleDownloadAllCards = async () => {
    if (!selectedEntityForCard) return;

    try {
      Swal.fire({
        title: 'Creating PDF',
        html: 'Preparing to generate PDF with all visiting cards...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Define all pages
      const pages = [
        {
          pageNumber: 0,
          title: 'Personalized Card',
          imageUrl: cardImage,
          isDynamic: true
        },
        {
          pageNumber: 1,
          title: 'Template 1',
          imageUrl: imgUrl.visitingCard2,
          isDynamic: false
        },
        {
          pageNumber: 2,
          title: 'Template 2',
          imageUrl: imgUrl.visitingCard3,
          isDynamic: false
        },
        {
          pageNumber: 3,
          title: 'Template 3',
          imageUrl: imgUrl.visitingCard4,
          isDynamic: false
        }
      ];

      // Filter out pages without image URLs
      const validPages = pages.filter(page => page.imageUrl);

      if (validPages.length === 0) {
        throw new Error('No visiting card images available');
      }

      let pdf = null;
      let firstImageLoaded = false;

      // Process each page
      for (let i = 0; i < validPages.length; i++) {
        const page = validPages[i];
        console.log(`Processing page ${i + 1}: ${page.title}`);

        Swal.update({
          html: `Processing ${page.title} (${i + 1}/${validPages.length})...`
        });

        // Fetch image blob
        const imageResponse = await fetch(page.imageUrl);
        if (!imageResponse.ok) {
          throw new Error(`Failed to fetch ${page.title}`);
        }
        const imageBlob = await imageResponse.blob();
        const base64 = await blobToBase64(imageBlob);

        // Load image and process
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Calculate high-quality dimensions
            const maxWidth = 1200;
            const maxHeight = 1200;

            let finalWidth = img.width;
            let finalHeight = img.height;

            if (finalWidth < maxWidth && finalHeight < maxHeight) {
              const scaleX = maxWidth / finalWidth;
              const scaleY = maxHeight / finalHeight;
              const scale = Math.min(scaleX, scaleY, 2);

              finalWidth = finalWidth * scale;
              finalHeight = finalHeight * scale;
            }

            // Create high-quality canvas
            const canvas = document.createElement('canvas');
            canvas.width = finalWidth;
            canvas.height = finalHeight;
            const ctx = canvas.getContext('2d');

            // Enable high-quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.webkitImageSmoothingEnabled = true;
            ctx.mozImageSmoothingEnabled = true;
            ctx.msImageSmoothingEnabled = true;

            // Draw image
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

            // Get high-quality base64
            const highQualityBase64 = canvas.toDataURL('image/png', 1.0);

            // Create or add page to PDF
            // Use portrait orientation to match visiting card images (325x532 = portrait)
            if (!firstImageLoaded) {
              // Calculate page size from image aspect ratio
              const imageAspectRatio = finalWidth / finalHeight;
              let pageWidth, pageHeight;

              if (imageAspectRatio < 1) {
                // Portrait image
                pageWidth = 50.8;  // 2 inches in mm
                pageHeight = 88.9; // 3.5 inches in mm
              } else {
                // Landscape image
                pageWidth = 88.9;  // 3.5 inches in mm
                pageHeight = 50.8; // 2 inches in mm
              }

              pdf = new jsPDF({
                orientation: imageAspectRatio < 1 ? "p" : "l",
                unit: "mm",
                format: [pageWidth, pageHeight],
                compress: false
              });
              firstImageLoaded = true;
            } else {
              // Determine orientation for this page
              const imageAspectRatio = finalWidth / finalHeight;
              if (imageAspectRatio < 1) {
                pdf.addPage([50.8, 88.9], "p"); // Portrait
              } else {
                pdf.addPage([88.9, 50.8], "l"); // Landscape
              }
            }

            // Calculate scaling - fill entire page
            const currentPageInfo = pdf.internal.pageSize;
            const pageWidth = currentPageInfo.getWidth();
            const pageHeight = currentPageInfo.getHeight();

            // Fill entire page without whitespace
            const scaledWidth = pageWidth;
            const scaledHeight = pageHeight;
            const xOffset = 0;
            const yOffset = 0;

            // Add image to PDF - fill entire page
            pdf.addImage(highQualityBase64, "PNG", xOffset, yOffset, scaledWidth, scaledHeight, undefined, 'FAST');

            resolve();
          };
          img.onerror = () => {
            reject(new Error(`Failed to load image for ${page.title}`));
          };
          img.src = base64;
        });
      }

      if (!pdf || !firstImageLoaded) {
        throw new Error('Failed to create PDF - no valid images found');
      }

      // Generate filename and download
      const peacekeeperId = selectedEntityForCard.peacekeeper_id || selectedEntityForCard.username || 'Entity';
      const filename = `VisitingCards-${peacekeeperId}.pdf`;

      Swal.update({
        html: 'Downloading PDF...'
      });

      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded!',
        text: `${validPages.length}-page visiting card PDF downloaded successfully`,
        timer: 2000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error creating PDF:', error);
      Swal.fire('Error', error.message || 'Failed to create PDF', 'error');
    }
  };

  // Handle downloading certificate
  const handleDownloadCertificate = () => {
    if (certificateImage && selectedEntityForCard) {
      const link = document.createElement('a');
      link.href = certificateImage;
      link.download = `Certificate-${selectedEntityForCard.name || selectedEntityForCard.peacekeeper_id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle regenerating visiting card
  const handleRegenerateCard = async () => {
    if (!selectedEntityForCard) return;

    const result = await Swal.fire({
      title: 'Regenerate Visiting Card?',
      text: 'This will create a new visiting card for this entity.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#214F7D',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, regenerate!',
      cancelButtonText: 'Cancel',
      customClass: {
        container: 'swal-z-index-high'
      }
    });

    if (!result.isConfirmed) return;

    setCardLoading(true);

    try {
      // Call the PUT endpoint to regenerate the visiting card
      const updateResponse = await apiServices.putAPI(
        `update-visiting-card/${selectedEntityForCard.peacekeeper_id}`,
        { forceRegenerate: true }
      );

      if (updateResponse.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Visiting card regeneration started. Fetching new card...',
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            container: 'swal-z-index-high'
          }
        });

        // Wait a moment for generation to complete, then fetch the new card
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`visiting-card/${selectedEntityForCard.userid}`);

            if (response.data) {
              // Revoke old blob URL to prevent memory leaks
              if (cardImage) {
                URL.revokeObjectURL(cardImage);
              }
              const blob = response.data;
              const blobUrl = URL.createObjectURL(blob);
              setCardImage(blobUrl);

              Swal.fire({
                icon: 'success',
                title: 'Complete!',
                text: 'Visiting card has been regenerated successfully.',
                timer: 2000,
                showConfirmButton: false,
                customClass: {
                  container: 'swal-z-index-high'
                }
              });
            }
          } catch (err) {
            console.error('Error fetching regenerated visiting card:', err);
            Swal.fire({
              icon: 'warning',
              title: 'Generated',
              text: 'Card regenerated but unable to load. Please close and reopen to view.',
              customClass: {
                container: 'swal-z-index-high'
              }
            });
          } finally {
            setCardLoading(false);
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Error regenerating visiting card:', err);
      setCardLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to regenerate visiting card. Please try again.',
        customClass: {
          container: 'swal-z-index-high'
        }
      });
    }
  };

  // Handle regenerating certificate
  const handleRegenerateCertificate = async () => {
    if (!selectedEntityForCard) return;

    const result = await Swal.fire({
      title: 'Regenerate Certificate?',
      text: 'This will create a new certificate for this entity.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#214F7D',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, regenerate!',
      cancelButtonText: 'Cancel',
      customClass: {
        container: 'swal-z-index-high'
      }
    });

    if (!result.isConfirmed) return;

    setCertificateLoading(true);

    try {
      // Call the PUT endpoint to regenerate the certificate
      const updateResponse = await apiServices.putAPI(
        `update-certificate/${selectedEntityForCard.peacekeeper_id}`,
        { forceRegenerate: true }
      );

      if (updateResponse.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Certificate regeneration started. Fetching new certificate...',
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            container: 'swal-z-index-high'
          }
        });

        // Wait a moment for generation to complete, then fetch the new certificate
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`certificate/${selectedEntityForCard.userid}`);

            if (response.data) {
              // Revoke old blob URL to prevent memory leaks
              if (certificateImage) {
                URL.revokeObjectURL(certificateImage);
              }
              const blob = response.data;
              const blobUrl = URL.createObjectURL(blob);
              setCertificateImage(blobUrl);

              Swal.fire({
                icon: 'success',
                title: 'Complete!',
                text: 'Certificate has been regenerated successfully.',
                timer: 2000,
                showConfirmButton: false,
                customClass: {
                  container: 'swal-z-index-high'
                }
              });
            }
          } catch (err) {
            console.error('Error fetching regenerated certificate:', err);
            Swal.fire({
              icon: 'warning',
              title: 'Generated',
              text: 'Certificate regenerated but unable to load. Please close and reopen to view.',
              customClass: {
                container: 'swal-z-index-high'
              }
            });
          } finally {
            setCertificateLoading(false);
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Error regenerating certificate:', err);
      setCertificateLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to regenerate certificate. Please try again.',
        customClass: {
          container: 'swal-z-index-high'
        }
      });
    }
  };

  // Generate referral link based on usertype and temp_peacekeeper_id
  const generateReferralLink = (entity) => {
    if (!entity) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = entity.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (entity.usertype === 'Entity' && entity.categoryname) {
      const cleanName = (entity.name || entity.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = entity.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (entity.temp_peacekeeper_id) {
      return `${domain}/register?${entity.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?referencenumber={peacekeeper_id}
    return `${domain}/register?${peacekeeperId}`;
  };

  // Handle copying referral link
  const handleCopyReferralLink = async (entity) => {
    if (!entity) return;

    const referralLink = generateReferralLink(entity);

    try {
      await navigator.clipboard.writeText(referralLink);
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Referral link copied to clipboard',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Referral link copied to clipboard',
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  // Convert entities to CSV format
  const convertEntitiesToCSV = (entities) => {
    if (!entities || entities.length === 0) {
      return [];
    }

    const headers = [
      // 'User ID',
      'IAPM ID',
      'Name',
      'Email',
      'Phone',
      'Country',
      'User Type',
      'Category',
      // 'Payment Status',
      'Gender',
      'Date of Birth',
      'Reference By',
      'Created Date'
    ];

    const dataRows = entities.map(entity => [
      entity.userid || '',
      entity.peacekeeper_id || '',
      entity.username || '',
      entity.email || '',
      entity.mobile || '',
      entity.country || '',
      entity.usertype || '',
      entity.categoryname || '',
      entity.payment_status || '',
      entity.gender || '',
      entity.dob || '',
      entity.reference_by || '',
      entity.created_at ? new Date(entity.created_at).toLocaleDateString() : ''
    ]);

    return [headers, ...dataRows];
  };

  // Download CSV file
  const downloadCSV = (entities, filename) => {
    try {
      const csvData = convertEntitiesToCSV(entities);

      const csvContent = csvData.map(row =>
        row.map(cell => {
          const cellStr = String(cell).replace(/"/g, '""');
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      console.log(`CSV file ${filename} downloaded successfully`);
    } catch (error) {
      console.error('Error creating CSV file:', error);
      setError('Error creating CSV file. Please try again.');
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  // Get active/inactive chip color (for activestatus)
  const getActiveStatusColor = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'Y':
        return 'success';
      case 'N':
        return 'error';
      default:
        return 'default';
    }
  };

  // Toggle entity active status (Y/N)
  const handleEntityStatusToggle = async (entity) => {
    const newStatus = entity.activestatus === 'Y' ? 'N' : 'Y';
    setUpdatingStatus(entity.userid);
    try {
      const response = await apiServices.patchAPI(`users/${entity.userid}/status`, { activestatus: newStatus });
      if (response.data && (response.data.success || response.status === 200)) {
        setEntities((prev) => prev.map((e) => e.userid === entity.userid ? { ...e, activestatus: newStatus } : e));
        setError('');
      } else {
        setError(response.data?.error || 'Failed to update entity status');
      }
    } catch (err) {
      console.error('Error updating entity status:', err);
      setError(err.response?.data?.error || err.message || 'Error updating entity status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Get usertype chip color
  const getUsertypeColor = (usertype) => {
    switch (usertype?.toLowerCase()) {
      case 'entity': return 'primary';
      case 'individual': return 'secondary';
      case 'corporate': return 'info';
      case 'organization': return 'warning';
      case 'government': return 'error';
      case 'educational': return 'success';
      default: return 'default';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch entities when dependencies change
  useEffect(() => {
    fetchEntities(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Fetch categories and countries on mount
  useEffect(() => {
    fetchCategories();
    fetchCountries();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchEntities(0, rowsPerPage);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pr: 4, mr: 4 }}>
      {/* Global styles for SweetAlert z-index */}
      <style>
        {`
          .swal-z-index-high {
            z-index: 1400 !important;
          }
        `}
      </style>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mr: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create and manage entities using enhanced user creation API
          </Typography>
        </Box>
        {/* Only show Add New Entity button if usertype is not Entity */}
        {usertype !== 'Entity' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            sx={{
              backgroundColor: '#3A8139',
              color: '#fff',
              '&:hover': { backgroundColor: '#2d6329' },
              px: 3,
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Add New Entity
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mr: 2 }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="flex-start">
            <div className='entityMgmt'>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search entities by name, email, phone, or PeaceKeeper ID..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#6c757d' }} />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2.5}>
                <FormControl size="small" sx={{ minWidth: '120px' }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Category"
                    sx={{
                      backgroundColor: '#fff',
                      '& .MuiSelect-select': { paddingRight: '32px' }
                    }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.categoryid} value={category.categoryname}>
                        {category.categoryname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  size="small"
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="yyyy-mm-dd"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    placeholder: 'yyyy-mm-dd'
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  size="small"
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="yyyy-mm-dd"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    placeholder: 'yyyy-mm-dd'
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#214F7D',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<FilterListIcon />}
                    onClick={handleApplyFilters}
                    size="small"
                    sx={{
                      backgroundColor: '#214F7D',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#1a3f63' },
                      px: 2, py: 1, borderRadius: '6px',
                      textTransform: 'none', fontWeight: 500, minWidth: '80px',
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleExportEntities}
                    size="small"
                    disabled={entities.length === 0}
                    sx={{
                      borderColor: '#28a745',
                      '&:hover': { borderColor: '#1e7e34', backgroundColor: 'rgba(40, 167, 69, 0.04)' },
                      px: 0, py: 0, borderRadius: '6px',
                      textTransform: 'none', fontWeight: 500, minWidth: '40px', justifyItems: 'center',
                    }}
                  ><DownloadIcon />
                  </Button>
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} size="small"
                      sx={{
                        border: '1px solid #dee2e6', backgroundColor: '#fff', width: '40px',
                        height: '40px',
                        '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#214F7D' },
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: '1.1rem', color: '#6c757d' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </div>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Entities Table */}
      <Paper sx={{ width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'auto', pr: 2, mr: 2 }}>
        <TableContainer sx={{ maxHeight: 500, overflowX: 'auto', overflowY: 'auto' }} className='referralsTable'>
          <Table
            stickyHeader
            sx={{
              minWidth: 1200,
              '& td, & th': { whiteSpace: 'nowrap' }
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>ID</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>IAPM ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Country</TableCell>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>User Type</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Payment Tier</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 180 }}>Individual Referral</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Entity Referral</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Status</TableCell>
                {/* <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Status</TableCell> */}
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Created Date</TableCell>
                {usertype !== 'Entity' && (
                  <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={usertype === 'Entity' ? 11 : 12} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Loading entities...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : entities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={usertype === 'Entity' ? 11 : 12} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No entities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entities.map((entity) => (
                  <TableRow key={entity.userid} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    {/* <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.875rem' }}>
                        #{entity.userid}
                      </Typography>
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.875rem' }}>
                        {entity.peacekeeper_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entity.username}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {entity.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entity.mobile}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {entity.country}
                      </Typography>
                    </TableCell>
                    {/* <TableCell sx={{ py: 1 }}>
                      {entity.usertype ? (
                        <Chip
                          label={entity.usertype}
                          color={getUsertypeColor(entity.usertype)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            '& .MuiChip-label': { fontSize: '0.75rem' }
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {entity.categoryname || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {entity.entity_payment_tier ? (
                        <Chip
                          label={entity.entity_payment_tier}
                          size="small"
                          sx={{
                            backgroundColor: entity.entity_payment_tier === 'PAID' ? '#e8f5e8' : entity.entity_payment_tier === 'UNPAID' ? '#fff3e0' : '#f5f5f5',
                            color: entity.entity_payment_tier === 'PAID' ? '#2e7d32' : entity.entity_payment_tier === 'UNPAID' ? '#f57c00' : '#666',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                          Not Set
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {entity.discount_coupon_percent ? `${entity.discount_coupon_percent}%` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {entity.entity_discount_percent ? `${entity.entity_discount_percent}%` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={entity.activestatus === 'Y' ? 'Active' : 'Inactive'}
                        color={getActiveStatusColor(entity.activestatus)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    {/* <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={entity.payment_status || 'PENDING'}
                        color={getStatusColor(entity.payment_status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }}
                      />
                    </TableCell> */}
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDate(entity.created_at)}
                      </Typography>
                    </TableCell>
                    {usertype !== 'Entity' && (
                      <TableCell align="center" sx={{ pr: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
                          <Tooltip title={entity.activestatus === 'Y' ? 'Deactivate Entity' : 'Activate Entity'}>
                            <Switch
                              checked={entity.activestatus === 'Y'}
                              onChange={() => handleEntityStatusToggle(entity)}
                              disabled={updatingStatus === entity.userid}
                              size="small"
                              color="success"
                            />
                          </Tooltip>
                          <Tooltip title="More Options">
                            <IconButton
                              size="small"
                              onClick={(event) => handleMenuOpen(event, entity)}
                              sx={{
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Summary */}
      <Box sx={{
        mt: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          <strong>Total Entities:</strong> {totalCount} | <strong>Showing:</strong> {entities.length} of {totalCount}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
        </Typography>
      </Box>

      {/* Create Entity Modal - Only show if usertype is not Entity */}
      {usertype !== 'Entity' && (
        <Dialog
          open={createModalOpen}
          onClose={handleCloseCreateModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{
            pb: 3,
            pt: 4,
            px: 4,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
              Create New Entity
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pb: 2, pt: '30px !important', px: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Row 1: Full Name & Email */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Full Name</Typography>
                  <TextField
                    fullWidth
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    required
                    placeholder="Enter Full Name"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Email</Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    required
                    placeholder="Enter Email"
                  />
                </Box>
              </Box>

              {/* Row 2: Phone Number & Nationality */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Phone Number</Typography>
                  <Box sx={{ display: 'flex', gap: 0 }}>
                    <FormControl sx={{ minWidth: 120 }}>
                      <Select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '4px 0 0 4px',
                            borderRight: 'none'
                          }
                        }}
                      >
                        {countryCodes.map((cc) => (
                          <MenuItem key={cc.code} value={cc.code}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                component="img"
                                src={`https://flagcdn.com/w20/${cc.country}.png`}
                                alt={cc.name}
                                sx={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px' }}
                              />
                              <span>{cc.code}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      placeholder="Enter phone number"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0 4px 4px 0',
                          borderLeft: 'none'
                        }
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Nationality</Typography>
                  <FormControl fullWidth required>
                    <Select
                      value={formData.country}
                      onChange={(e) => handleFormChange('country', e.target.value)}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id || country.countryname} value={country.countryname}>
                          {country.countryname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Row 3: Category & Payment Tier */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Category</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.categoryname}
                      onChange={(e) => handleFormChange('categoryname', e.target.value)}
                    >
                      <MenuItem value="">No Category</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.categoryid} value={category.categoryname}>
                          {category.categoryname} ({category.status})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Payment Tier</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.entity_payment_tier}
                      onChange={(e) => handleFormChange('entity_payment_tier', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Not Set</MenuItem>
                      <MenuItem value="PAID">PAID</MenuItem>
                      <MenuItem value="UNPAID">UNPAID</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Row 4: Referral Fields */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Entity Referral %</Typography>
                  <TextField
                    fullWidth
                    value={formData.entity_discount_percent}
                    onChange={(e) => handleFormChange('entity_discount_percent', e.target.value)}
                    placeholder="e.g., 15"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Individual Referral %</Typography>
                  <TextField
                    fullWidth
                    value={formData.discount_coupon_percent}
                    onChange={(e) => handleFormChange('discount_coupon_percent', e.target.value)}
                    placeholder="e.g., 10"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Box>
              </Box>

              {/* Email and Website Visibility Checkbox */}
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  sx={{ textAlign: "left" }}
                  className='formCheckControl'
                  control={
                    <Checkbox
                      checked={formData.mobile_email_flag}
                      onChange={(e) => handleFormChange('mobile_email_flag', e.target.checked)}
                    />
                  }
                  label="Do you want the email and phone number to be visible on the Certificate? If Yes, Please tick the checkbox."
                />
              </Box>

              {/* Profile Image Upload */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ textAlign: "left", mb: 1, fontWeight: 500 }}>Profile Image</Typography>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#3A8139',
                      backgroundColor: '#f0f8f0'
                    }
                  }}
                  onClick={() => document.getElementById('profile-image-upload').click()}
                >
                  {profileImagePreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={profileImagePreview}
                        alt="Profile Preview"
                        sx={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: 1,
                          mx: 'auto',
                          display: 'block'
                        }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Click to change image
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        📷 Click to upload profile image *
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Required: JPEG, PNG, GIF, WebP (Max 5MB, Min 50x50px)
                      </Typography>
                    </>
                  )}
                </Box>
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                {imageUploadError && (
                  <Typography color="error" sx={{ mt: 0.5, fontSize: '12px', textAlign: 'left' }}>
                    {imageUploadError}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{
            px: 4,
            py: 3,
            pt: 3,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa'
          }}>
            <Button
              onClick={handleCloseCreateModal}
              disabled={creating}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: '#ccc',
                color: '#666'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateEntity}
              variant="contained"
              disabled={creating || !formData.name.trim() || !formData.email.trim() || !phoneNumber.trim() || !formData.country.trim() || !profileImage}
              startIcon={creating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              sx={{
                minWidth: 140,
                backgroundColor: '#214F7D',
                '&:hover': {
                  backgroundColor: '#1a3f63'
                }
              }}
            >
              {creating ? 'Creating...' : 'Create Entity'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* View Entity Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Entity Details
          </Typography>
          {selectedEntity && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedEntity.username} ({selectedEntity.peacekeeper_id})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          {selectedEntity && (
            <Grid container spacing={3}>
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>#{selectedEntity.userid}</Typography>
              </Grid> */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">IAPM ID</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: '#214F7D' }}>{selectedEntity.peacekeeper_id}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.username}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.mobile}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.country}</Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">User Type</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedEntity.usertype ? (
                    <Chip label={selectedEntity.usertype} color={getUsertypeColor(selectedEntity.usertype)} size="small" />
                  ) : (
                    'N/A'
                  )}
                </Typography>
              </Grid> */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.categoryname || 'N/A'}</Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Chip 
                    label={selectedEntity.payment_status || 'PENDING'} 
                    color={getStatusColor(selectedEntity.payment_status)} 
                    size="small" 
                  />
                </Typography>
              </Grid> */}
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Payment Type</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.payment_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.gender || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.dob || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Entity Discount %</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.entity_discount_percent || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Coupon Discount %</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.discount_coupon_percent || 'N/A'}</Typography>
              </Grid> */}
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Reference By</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedEntity.reference_by || 'N/A'}</Typography>
              </Grid> */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{formatDate(selectedEntity.created_at)}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          pt: 3,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={handleCloseViewModal}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entity Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Edit Entity
          </Typography>
          {selectedEntity && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedEntity.username} ({selectedEntity.peacekeeper_id})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '30px !important', px: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Row 1: Full Name & Email */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Full Name</Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  disabled
                  placeholder="Enter Full Name"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Email</Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder="Enter Email"
                  disabled
                  helperText="Email cannot be changed"
                />
              </Box>
            </Box>

            {/* Row 2: Phone Number & Nationality */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Phone Number</Typography>
                <TextField
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  placeholder="Enter phone number with country code"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Nationality</Typography>
                <FormControl fullWidth>
                  <Select
                    disabled={true}
                    value={formData.country}
                    onChange={(e) => handleFormChange('country', e.target.value)}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id || country.countryname} value={country.countryname}>
                        {country.countryname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Row 3: Category & Payment Tier */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Category</Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.categoryname}
                    onChange={(e) => handleFormChange('categoryname', e.target.value)}
                  >
                    <MenuItem value="">No Category</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.categoryid} value={category.categoryname}>
                        {category.categoryname} ({category.status})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Payment Tier</Typography>
                <FormControl fullWidth>
                  <Select
                    disabled={true}
                    value={formData.entity_payment_tier}
                    onChange={(e) => handleFormChange('entity_payment_tier', e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">Not Set</MenuItem>
                    <MenuItem value="PAID">PAID</MenuItem>
                    <MenuItem value="UNPAID">UNPAID</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Row 4: Referral Fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Entity Referral %</Typography>
                <TextField
                  fullWidth
                  value={formData.entity_discount_percent}
                  onChange={(e) => handleFormChange('entity_discount_percent', e.target.value)}
                  placeholder="e.g., 15"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Individual Referral %</Typography>
                <TextField
                  fullWidth
                  value={formData.discount_coupon_percent}
                  onChange={(e) => handleFormChange('discount_coupon_percent', e.target.value)}
                  placeholder="e.g., 10"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Box>
            </Box>

            {/* Email and Website Visibility Checkbox */}
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                sx={{ textAlign: "left" }}
                className='formCheckControl'
                control={
                  <Checkbox
                    checked={formData.mobile_email_flag}
                    onChange={(e) => handleFormChange('mobile_email_flag', e.target.checked)}
                  />
                }
                label="Do you want the email and phone number to be visible on the Certificate? If Yes, Please tick the checkbox."
              />
            </Box>

            {/* Row 5: Additional Fields */}
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Date of Birth</Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleFormChange('dob', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Gender</Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.gender}
                    onChange={(e) => handleFormChange('gender', e.target.value)}
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                    <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box> */}

            {/* Row 6: Payment Type */}
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Payment Type</Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.payment_type}
                    onChange={(e) => handleFormChange('payment_type', e.target.value)}
                  >
                    <MenuItem value="">Select Payment Type</MenuItem>
                    <MenuItem value="Stripe">Stripe</MenuItem>
                    <MenuItem value="PayPal">PayPal</MenuItem>
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="Credit Card">Credit Card</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>User Type</Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.usertype}
                    onChange={(e) => handleFormChange('usertype', e.target.value)}
                  >
                    <MenuItem value="Entity">Entity</MenuItem>
                    <MenuItem value="Individual">Individual</MenuItem>
                    <MenuItem value="Corporate">Corporate</MenuItem>
                    <MenuItem value="Organization">Organization</MenuItem>
                    <MenuItem value="Educational">Educational</MenuItem>
                    <MenuItem value="Government">Government</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box> */}

            {/* Profile Image Upload */}
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ textAlign: "left", mb: 1, fontWeight: 500 }}>Profile Image</Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  cursor: loadingCurrentImage ? 'default' : 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': !loadingCurrentImage ? {
                    borderColor: '#214F7D',
                    backgroundColor: '#f0f4f8'
                  } : {}
                }}
                onClick={() => !loadingCurrentImage && document.getElementById('edit-profile-image-upload').click()}
              >
                {loadingCurrentImage ? (
                  <Box sx={{ py: 3 }}>
                    <CircularProgress size={40} sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Loading current image...
                    </Typography>
                  </Box>
                ) : editProfileImagePreview ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={editProfileImagePreview}
                      alt="New Profile Preview"
                      sx={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: 1,
                        mx: 'auto',
                        display: 'block',
                        border: '2px solid #3A8139'
                      }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#3A8139', fontWeight: 600 }}>
                      ✓ New image selected - Click to change
                    </Typography>
                  </Box>
                ) : currentProfileImage ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={currentProfileImage}
                      alt="Current Profile"
                      sx={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: 1,
                        mx: 'auto',
                        display: 'block',
                        border: '2px solid #214F7D'
                      }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#214F7D' }}>
                      Current profile image - Click to change
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      📷 Click to upload profile image
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPEG, PNG, GIF, WebP (Max 5MB, Min 50x50px)
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: '#999' }}>
                      No profile image found
                    </Typography>
                  </>
                )}
              </Box>
              <input
                id="edit-profile-image-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleEditImageUpload}
                style={{ display: 'none' }}
              />
              {editImageUploadError && (
                <Typography color="error" sx={{ mt: 0.5, fontSize: '12px', textAlign: 'left' }}>
                  {editImageUploadError}
                </Typography>
              )}
              {currentProfileImage && !editProfileImagePreview && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', textAlign: 'center', fontStyle: 'italic' }}>
                  Leave as is to keep current image, or upload new image to replace
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          pt: 3,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={handleCloseEditModal}
            disabled={updating}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateEntity}
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{
              minWidth: 140,
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3f63'
              }
            }}
          >
            {updating ? 'Updating...' : 'Update Entity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visiting Card Modal */}
      <Dialog
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Visiting Card - {selectedEntityForCard?.name || selectedEntityForCard?.username}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 3, pt: '24px !important', px: 3, textAlign: 'center', minHeight: '400px' }}>
          {/* Page Navigation */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            gap: 1,
            alignItems: 'center'
          }}>
            {/* Previous Button */}
            <IconButton
              onClick={prevCardPage}
              disabled={cardLoading}
              size="small"
              sx={{
                border: '1px solid #e0e0e0',
                '&:hover': { backgroundColor: '#f0f0f0' }
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            {/* Page Dots */}
            {[0, 1, 2, 3].map((page) => (
              <Box
                key={page}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: currentCardPage === page ? '#3A8139' : '#e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setCurrentCardPage(page)}
              />
            ))}

            {/* Next Button */}
            <IconButton
              onClick={nextCardPage}
              disabled={cardLoading}
              size="small"
              sx={{
                border: '1px solid #e0e0e0',
                '&:hover': { backgroundColor: '#f0f0f0' }
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>

          {/* Card Image Display */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            {cardLoading && currentCardPage === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={50} />
                <Typography variant="body2" color="text.secondary">
                  Loading visiting card...
                </Typography>
              </Box>
            ) : (
              <>
                {currentCardPage === 0 ? (
                  // Dynamic card from API
                  cardImage ? (
                    <Box
                      component="img"
                      src={cardImage}
                      alt="Visiting Card (Personalized)"
                      sx={{
                        maxWidth: '325px',
                        height: '532px',
                        objectFit: 'contain',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No personalized visiting card available
                    </Typography>
                  )
                ) : currentCardPage === 1 ? (
                  // Static card 2
                  <Box
                    component="img"
                    src={imgUrl.visitingCard2}
                    alt="Visiting Card Template 1"
                    sx={{
                      maxWidth: '325px',
                      height: '532px',
                      objectFit: 'contain',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                ) : currentCardPage === 2 ? (
                  // Static card 3
                  <Box
                    component="img"
                    src={imgUrl.visitingCard3}
                    alt="Visiting Card Template 2"
                    sx={{
                      maxWidth: '325px',
                      height: '532px',
                      objectFit: 'contain',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                ) : (
                  // Static card 4
                  <Box
                    component="img"
                    src={imgUrl.visitingCard4}
                    alt="Visiting Card Template 3"
                    sx={{
                      maxWidth: '325px',
                      height: '532px',
                      objectFit: 'contain',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                )}
              </>
            )}
          </Box>

          {/* Card Type Label */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {currentCardPage === 0 ? 'Personalized Card (Generated)' : `Template ${currentCardPage}`}
          </Typography>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={handleRegenerateCard}
            disabled={cardLoading}
            variant="outlined"
            startIcon={<RefreshIcon />}
            sx={{
              minWidth: 130,
              borderColor: '#FF9800',
              color: '#FF9800',
              '&:hover': {
                borderColor: '#F57C00',
                backgroundColor: 'rgba(255, 152, 0, 0.08)'
              }
            }}
          >
            Regenerate
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleDownloadAllCards}
              disabled={cardLoading}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                minWidth: 140,
                backgroundColor: '#3A8139',
                '&:hover': {
                  backgroundColor: '#2d6329'
                }
              }}
            >
              Download All (PDF)
            </Button>
            <Button
              onClick={handleDownloadCard}
              disabled={(currentCardPage === 0 && !cardImage) || cardLoading}
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{
                minWidth: 120,
                borderColor: '#214F7D',
                color: '#214F7D',
                '&:hover': {
                  borderColor: '#1a3f63',
                  backgroundColor: 'rgba(33, 79, 125, 0.08)'
                }
              }}
            >
              Download Current
            </Button>
            <Button
              onClick={() => setCardModalOpen(false)}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: '#ccc',
                color: '#666'
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Certificate Modal */}
      <Dialog
        open={certificateModalOpen}
        onClose={() => setCertificateModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Certificate - {selectedEntityForCard?.name || selectedEntityForCard?.username}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 3, pt: '24px !important', px: 3, textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {certificateLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={50} />
              <Typography variant="body2" color="text.secondary">
                Loading certificate...
              </Typography>
            </Box>
          ) : certificateImage ? (
            <Box
              component="img"
              src={certificateImage}
              alt="Certificate"
              sx={{
                maxWidth: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            />
          ) : (
            <Typography variant="body1" color="text.secondary">
              No certificate available
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={handleRegenerateCertificate}
            disabled={certificateLoading}
            variant="outlined"
            startIcon={<RefreshIcon />}
            sx={{
              minWidth: 130,
              borderColor: '#FF9800',
              color: '#FF9800',
              '&:hover': {
                borderColor: '#F57C00',
                backgroundColor: 'rgba(255, 152, 0, 0.08)'
              }
            }}
          >
            Regenerate
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleDownloadCertificate}
              disabled={!certificateImage || certificateLoading}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                minWidth: 120,
                backgroundColor: '#214F7D',
                '&:hover': {
                  backgroundColor: '#1a3f63'
                }
              }}
            >
              Download
            </Button>
            <Button
              onClick={() => setCertificateModalOpen(false)}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: '#ccc',
                color: '#666'
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Entity Referrals Modal */}
      <Dialog
        open={referralsModalOpen}
        onClose={() => setReferralsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#214F7D' }}>
            Entity Referrals - {selectedEntityForReferrals?.name || selectedEntityForReferrals?.username}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            {/* {selectedEntityForReferrals?.peacekeeper_id} |  */}
            {selectedEntityForReferrals?.categoryname || 'No Category'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 3, pt: '24px !important', px: 3 }}>
          {referralsLoading && !referralsData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={50} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Loading referrals...
                </Typography>
              </Box>
            </Box>
          ) : referralsError && !referralsData ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                {referralsError}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => selectedEntityForReferrals && fetchEntityReferrals(selectedEntityForReferrals.peacekeeper_id, referralsPage, referralsRowsPerPage)}
                size="medium"
              >
                Retry
              </Button>
            </Box>
          ) : referralsData ? (
            <>
              {/* Statistics Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={2.4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      ${referralsData.statistics?.totalDue?.toFixed(2) || '0.00'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Entity Due
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {referralsData.statistics?.entityDiscountPercent || 0}% of ${referralsData.statistics?.pricePerUser || 21}
                    </Typography> */}
                    {/* <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      L1: ${referralsData.statistics?.level1?.due?.toFixed(2) || '0.00'} | L2: ${referralsData.statistics?.level2?.due?.toFixed(2) || '0.00'}
                    </Typography> */}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', height: '100%' }}>
                    <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      {referralsData.statistics?.totalReferrals || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Total Referrals
                    </Typography>
                    {/* <Typography variant="caption" sx={{ color: '#1976d2', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      L1: {referralsData.statistics?.level1?.totalReferrals || 0} | L2: {referralsData.statistics?.level2?.totalReferrals || 0}
                    </Typography> */}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e8', border: '1px solid #c8e6c9', height: '100%' }}>
                    <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {referralsData.statistics?.paidReferrals || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Paid
                    </Typography>
                    {/* <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      L1: {referralsData.statistics?.level1?.paidReferrals || 0} | L2: {referralsData.statistics?.level2?.paidReferrals || 0}
                    </Typography> */}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', height: '100%' }}>
                    <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                      {referralsData.statistics?.pendingReferrals || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Pending
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', height: '100%' }}>
                    <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      {referralsData.statistics?.activeReferrals || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Active
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Filters */}
              <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                <CardContent sx={{ py: 2, px: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        placeholder="Search by name, email, or peacekeeper ID..."
                        value={referralsSearchTerm}
                        onChange={(e) => setReferralsSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#6c757d' }} />
                            </InputAdornment>
                          ),
                        }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff'
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2.5} className="paymentField">
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                          value={referralsPaymentFilter}
                          onChange={(e) => setReferralsPaymentFilter(e.target.value)}
                          label="Payment Status"
                          sx={{ backgroundColor: '#fff' }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="COMPLETED">Paid</MenuItem>
                          <MenuItem value="PENDING">Pending</MenuItem>
                          <MenuItem value="COMPLIMENTARY">Complimentary</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2.5} className="paymentField">
                      <FormControl fullWidth size="small">
                        <InputLabel>Active Status</InputLabel>
                        <Select
                          value={referralsActiveFilter}
                          onChange={(e) => setReferralsActiveFilter(e.target.value)}
                          label="Active Status"
                          sx={{ backgroundColor: '#fff' }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="Y">Active</MenuItem>
                          <MenuItem value="N">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<FilterListIcon />}
                          onClick={handleApplyReferralsFilters}
                          size="small"
                          sx={{
                            backgroundColor: '#214F7D',
                            '&:hover': { backgroundColor: '#1a3f63' },
                            px: 2, py: 1, borderRadius: '6px',
                            textTransform: 'none', fontWeight: 500
                          }}
                        >
                          Apply
                        </Button>
                        <Tooltip title="Refresh">
                          <IconButton onClick={handleRefreshReferrals} size="small"
                            sx={{
                              border: '1px solid #dee2e6',
                              backgroundColor: '#fff',
                              '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#214F7D' }
                            }}
                          >
                            <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Referrals Table */}
              <TableContainer component={Paper} sx={{ maxHeight: 400 }} className='referralsTable'>
                <Table
                  stickyHeader
                  sx={{
                    '& td, & th': { whiteSpace: 'nowrap' }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>IAPM ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                      {usertype !== 'Entity' && <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Email</TableCell>}
                      {usertype !== 'Entity' && <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Mobile</TableCell>}
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Country</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Individual Referral</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Individual Register</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Total Due</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Payment</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#214F7D', backgroundColor: '#f8f9fa' }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referralsLoading ? (
                      <TableRow>
                        <TableCell colSpan={usertype === 'Entity' ? 9 : 11} align="center">
                          <Box sx={{ py: 3 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : referralsData.referrals && referralsData.referrals.length > 0 ? (
                      referralsData.referrals.map((referral) => (
                        <TableRow key={referral.userId} hover>
                          <TableCell>{referral.name}-{selectedEntityForReferrals?.categoryname || 'N/A'}-{selectedEntityForReferrals?.name || selectedEntityForReferrals?.username || 'N/A'}-{referral.peacekeeperId || 'N/A'}</TableCell>
                          <TableCell>{referral.name || 'N/A'}</TableCell>

                          {usertype !== 'Entity' && <TableCell>{referral.email || 'N/A'}</TableCell>}
                          {usertype !== 'Entity' && <TableCell>{referral.mobile || 'N/A'}</TableCell>}
                          <TableCell>{referral.country || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#214F7D' }}>
                              {referral.discountPercent || 0}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {referral.subReferralsCount || 0}
                              {/* <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {referral.subReferralsCount || 0} total
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                                {referral.subReferralsPaid || 0} paid
                              </Typography> */}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                              ${referral.totalDue?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={referral.paymentStatus === 'COMPLETED' ? 'Paid' : referral.paymentStatus === 'COMPLIMENTARY' ? 'COMPLIMENTARY' : referral.paymentStatus || 'N/A'}
                              size="small"
                              sx={{
                                backgroundColor: referral.paymentStatus === 'COMPLETED' ? '#e8f5e8' :
                                  referral.paymentStatus === 'COMPLIMENTARY' ? '#e3f2fd' :
                                    referral.paymentStatus === 'PENDING' ? '#fff3e0' : '#f5f5f5',
                                color: referral.paymentStatus === 'COMPLETED' ? '#2e7d32' :
                                  referral.paymentStatus === 'COMPLIMENTARY' ? '#1976d2' :
                                    referral.paymentStatus === 'PENDING' ? '#f57c00' : '#666',
                                fontWeight: 500,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={referral.activeStatus === 'Y' ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                backgroundColor: referral.activeStatus === 'Y' ? '#e8f5e8' : '#ffebee',
                                color: referral.activeStatus === 'Y' ? '#2e7d32' : '#c62828',
                                fontWeight: 500,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(referral.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={usertype === 'Entity' ? 9 : 11} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No referrals found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {referralsData.referrals && referralsData.referrals.length > 0 && (
                <TablePagination
                  component="div"
                  count={referralsData.pagination?.totalCount || 0}
                  page={referralsPage}
                  onPageChange={handleReferralsPageChange}
                  rowsPerPage={referralsRowsPerPage}
                  onRowsPerPageChange={handleReferralsRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#f8f9fa'
                  }}
                />
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No data available
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={() => setReferralsModalOpen(false)}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* More Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            minWidth: 200,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => selectedEntityForMenu && handleViewReferrals(selectedEntityForMenu)} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <GroupsIcon fontSize="small" sx={{ color: '#3A8139' }} />
          </ListItemIcon>
          <ListItemText
            primary="View Referrals"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => selectedEntityForMenu && handleCopyReferralLink(selectedEntityForMenu)} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ContentCopyIcon fontSize="small" sx={{ color: '#FF9800' }} />
          </ListItemIcon>
          <ListItemText
            primary="Copy Referral Link"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => selectedEntityForMenu && handleViewCard(selectedEntityForMenu)} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CardIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText
            primary="View Visiting Card"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => selectedEntityForMenu && handleViewCertificate(selectedEntityForMenu)} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CertificateIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText
            primary="View Certificate"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
        <MenuItem onClick={handleViewEntityFromMenu} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ViewIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText
            primary="View Entity Details"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
        <MenuItem onClick={handleEditEntityFromMenu} sx={{ py: 1, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Edit Entity"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EntityManagement;

